import { generateRequestId, REQUEST_ID_HEADER } from "~vue/utils/requestId";

import { getCookie } from "../../utils";
import type { WorkDocumentStreamEvent } from "./workDocumentTypes";

async function responseError(response: Response): Promise<Error> {
    try {
        const payload = (await response.json()) as { error?: string; detail?: string };
        return new Error(payload.error || payload.detail || response.statusText);
    } catch {
        return new Error(response.statusText || "The document could not be read. Try again.");
    }
}

function eventFromFrame(frame: string): WorkDocumentStreamEvent | null {
    const data = frame
        .split("\n")
        .find((line) => line.startsWith("data: "))
        ?.slice(6);
    if (!data) {
        return null;
    }
    return JSON.parse(data) as WorkDocumentStreamEvent;
}

export async function streamWorkDocument(url: string, body: FormData, onEvent: (event: WorkDocumentStreamEvent) => void, signal: AbortSignal): Promise<void> {
    const headers: Record<string, string> = {
        Accept: "text/event-stream, application/json",
        [REQUEST_ID_HEADER]: generateRequestId(),
    };
    const csrfToken = getCookie("csrftoken");
    if (csrfToken) {
        headers["X-CSRFToken"] = csrfToken;
    }

    // Native fetch is required for incremental ReadableStream access.
    const response = await fetch(url, { method: "POST", body, headers, credentials: "same-origin", signal });
    if (!response.ok) {
        throw await responseError(response);
    }
    const reader = response.body?.getReader();
    if (!reader) {
        throw new Error("The document reading ended before it was complete. Try again.");
    }

    const decoder = new TextDecoder();
    let buffer = "";
    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                break;
            }
            buffer += decoder.decode(value, { stream: true });
            const frames = buffer.split("\n\n");
            buffer = frames.pop() ?? "";
            for (const frame of frames) {
                const event = eventFromFrame(frame);
                if (event) {
                    onEvent(event);
                }
            }
        }
    } finally {
        reader.releaseLock();
    }
}
